import React from 'react';
import Link from 'next/link';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Typography } from '@vibTheme/components/_base/Typography';
import { ProfitIndicator } from '../ProfitIndicator';
import {
  PaymentConversionFiltersInput,
  usePaymentConversion,
} from './usePaymentConversion';
import { MetricCard, formatRate } from './shared';
import {
  ReportLinkFilters,
  reportLinkHref,
} from '@utils/reportLinkParams';
import { ReportUnavailable } from '../ReportUnavailable';

type Props = { filters: PaymentConversionFiltersInput };

export const CONVERSION_REPORT_PATH = '/reports/conversion';

/**
 * Builds the drill-down link carrying the selection the producer is looking at.
 *
 * A bare link would open the detail on its own default period and all communities, so the breakdown would
 * not explain the card that was clicked - it would silently describe a different cohort. The named period
 * travels when there is one; the resolved dates travel always, so a custom range survives too.
 */
export const conversionReportHref = (filters: ReportLinkFilters) =>
  reportLinkHref(CONVERSION_REPORT_PATH, filters);

/**
 * Dashboard summary: the payment rate and nothing else.
 *
 * The detail - per method, time to pay, value paid over generated - lives on the conversion screen. The
 * root page already carries eight sales cards, a history chart and the community metrics, and adding a
 * second table to it buries the numbers a producer opens the page for.
 */
export function PaymentConversionSummary({ filters }: Props) {
  const { totals, priorTotals, isLoading, hasFailed } =
    usePaymentConversion(filters);

  return (
    <section className="pb-main">
      <div className="pb-main flex items-baseline justify-between gap-main">
        <div>
          <Typography variant="h2">Pagamento das cobranças</Typography>
          <Typography className="text-sm text-lightBlack">
            Das cobranças geradas no período, quantas foram pagas
          </Typography>
        </div>
        <Link
          href={conversionReportHref(filters)}
          className="text-sm text-primary whitespace-nowrap flex items-center gap-[4px]"
        >
          Ver detalhes
          <ArrowForwardIcon fontSize="small" />
        </Link>
      </div>

      {/*
       * A failure would otherwise draw 0% with "0 pagas de 0 geradas" underneath, which reads as a period
       * where nobody paid rather than as a number that could not be fetched.
       */}
      {hasFailed ? (
        <ReportUnavailable />
      ) : (
      <div className="grid gap-main md:grid-cols-2 2xl:grid-cols-4">
        <MetricCard
          title="Taxa de pagamento"
          isLoading={isLoading}
          value={formatRate(totals.paidRate)}
          caption={`${totals.paidCount} pagas de ${totals.createdCount} geradas`}
          indicator={
            priorTotals && (
              <ProfitIndicator
                current={totals.paidRate}
                previous={priorTotals.paidRate}
              />
            )
          }
          help="Das cobranças geradas no período selecionado, o percentual que foi pago. A cobrança entra na conta pela data em que foi gerada, então uma cobrança de ontem paga hoje continua contando no dia de ontem — é por isso que esse número não é o mesmo do card de vendas. Veja a quebra por método de pagamento em Relatórios › Conversão."
        />
      </div>
      )}
    </section>
  );
}
